import * as Yup from 'yup'
import { hasHtmlText } from '../../../../../../utils/sanitizeRichText'
import { FormikErrors, yupToFormErrors } from 'formik'
import { COMMUNITY_STAKEHOLDER_KEY, FINANCIAL_ESTIMATE_KEY, OBJECTIVES_TABS, PROBLEM_TREE_TABS, PROCUREMENT_TABS, RESOURCE_TABS, PROJECT_OVERVIEW_SECTIONS, STAKEHOLDER_TABS, STEP_TABS, type ProjectOverviewSection } from './_models'

export const PROJECT_NAME_MAX_LENGTH = 250
export const PROJECT_SHORT_NAME_MAX_LENGTH = 50
export const PROJECT_DESC_MAX_LENGTH = 150

export const maxCharsMessage = (label: string, maxLength: number): string =>
    `${label} must be ${maxLength} characters or less`

const requiredString = (label: string, maxLength?: number) => {
    let schema = Yup.string().trim().required().label(label)

    if (maxLength !== undefined) {
        schema = schema.max(maxLength, maxCharsMessage(label, maxLength))
    }

    return schema
}

// Rules for the objectives, outcomes and SMART indicators lists, shared by every stage that shows them.
// Popup rows are validated by the API on save; these only need them to exist.
export const projectObjectivesRule = () => Yup.array()
    .min(1, 'At least one project objective must be added')
    .required()
    .label('Project Objectives')

export const projectOutcomesRule = () => Yup.array()
    .min(1, 'At least one project outcome must be added')
    .required()
    .label('Project Outcomes')

// Indicators captured before timeframes existed must be completed; the target date is optional.
export const smartIndicatorsRule = () => Yup.array()
    .of(
        Yup.object({
            target_timeframe_years: Yup.number().nullable().required('Add a timeframe to every SMART indicator').label('Timeframe'),
        })
    )
    .min(1, 'At least one SMART indicator must be added')
    .required()
    .label('SMART Indicators')

// Largest project cost amount. The API column allows more, but past this a
// JavaScript number can no longer hold every cent exactly.
export const MAX_COST_AMOUNT = 9999999999999.99

const optionalAmount = (label: string) =>
    Yup.number()
        .transform((value, original) => (original === '' || original === null ? undefined : value))
        .typeError(`${label} must be a number`)
        .min(0, `${label} cannot be negative`)
        .max(MAX_COST_AMOUNT, `${label} is too large`)
        .label(label)

const requiredArray = (label: string, min = 1) =>
    Yup.array()
        .of(Yup.string())
        .min(min, `At least ${min} ${label} must be selected`)
        .required()

// Each Project Overview tab is saved on its own, so only that tab is validated.
export const projectOverviewSectionSchemas: Record<ProjectOverviewSection, Yup.AnyObjectSchema> = {
    basic_information: Yup.object({
        name: requiredString('Project Name', PROJECT_NAME_MAX_LENGTH),
        short_name: requiredString('Short Name', PROJECT_SHORT_NAME_MAX_LENGTH),
        priority_id: requiredString('Timeline Priority'),
        dept_programme_id: requiredString('Programme'),
        project_type_data_key: requiredArray('Project Type'),
        desc: requiredString('Short Description', PROJECT_DESC_MAX_LENGTH),
        implementation_timeframe: requiredString('Implementation Timeframe'),
        capax: optionalAmount('CAPEX'),
        opax: optionalAmount('OPEX'),
        ballpark_project_cost: optionalAmount('Ballpark Project Cost'),
    }),
    sector_classification: Yup.object({
        sector_id: requiredString('Sector'),
        sub_sector_id: requiredString('Sub Sector'),
        action_project_type_id: requiredString('Action Project Type'),
        sector_asset_type_id: requiredArray('Asset Type'),
    }),
    idms_stage: Yup.object({
        idms_project_stage: requiredString('IDMS Project Stage'),
    }),
    accountable_ownership: Yup.object({
        jurisdiction_id: requiredString('Jurisdiction'),
        // location_id: requiredString('Location'),
        district_id: requiredString('District Municipality'),
        project_owner: requiredString('Department'),
    }),
}

// The first Project Overview tab, in display order, that fails validation, with its errors.
export async function findIncompleteOverviewSection(values: object): Promise<{ section: ProjectOverviewSection; errors: FormikErrors<object> } | null> {
    for (const section of PROJECT_OVERVIEW_SECTIONS) {
        try {
            await projectOverviewSectionSchemas[section].validate(values, { abortEarly: false })
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                return { section, errors: yupToFormErrors(error) }
            }
            throw error
        }
    }
    return null
}

// Longest value of a short (string) project field the API stores.
const SHORT_FIELD_MAX_LENGTH = 500

// An optional whole number from 0, e.g. an estimated job count.
const optionalCount = (label: string) =>
    Yup.number()
        .transform((value, original) => (original === '' || original === null ? undefined : value))
        .typeError(`${label} must be a number`)
        .integer(`${label} must be a whole number`)
        .min(0, `${label} cannot be negative`)
        .label(label)

// Selected options of a multi-select must each have a remark.
const remarkPerOption = (optionsField: string) =>
    Yup.object().test('option-remarks', 'Remarks are required', function (remarks) {
        const selected = (this.parent[optionsField] ?? []) as string[]
        const missing = selected.find((key) => !hasHtmlText((remarks as Record<string, string> | undefined)?.[key]))
        return missing
            ? this.createError({ path: `${this.path}.${missing}`, message: 'Remark is required' })
            : true
    })

// Tab-wise steps save each tab on its own, so only the open tab is validated. Keyed by step, then tab id.
export const stepTabSchemas: Partial<Record<string, Record<number, Yup.AnyObjectSchema>>> = {
    problem_tree_analysis: {
        [PROBLEM_TREE_TABS.focal_problem]: Yup.object({
            focal_problem: requiredString('Main / Focal Problem'),
        }),
        [PROBLEM_TREE_TABS.effect_tree]: Yup.object({
            consequences_if_problem_remains_unaddressed: requiredString('Consequences If the Problem Remains Unaddressed'),
        }),
        [PROBLEM_TREE_TABS.summary]: Yup.object({
            comprehensive_problem_tree_narrative: requiredString('Comprehensive Problem Tree Narrative'),
        }),
        [PROBLEM_TREE_TABS.solution_direction]: Yup.object({
            solution_directions_high_level: requiredArray('solution direction').label('Solution Directions (High-Level)'),
        }),
    },
    stakeholder_analysis_and_consultation: {
        [STAKEHOLDER_TABS.stakeholder_validation]: Yup.object({
            stakeholder_validation: requiredArray('stakeholder').label('Stakeholders Who Validated This Analysis'),
            stakeholder_validation_remarks: Yup.object().test(
                'community-engagement-method',
                'Community Engagement Method is required',
                function (remarks) {
                    const selected = (this.parent.stakeholder_validation ?? []) as string[]
                    if (!selected.includes(COMMUNITY_STAKEHOLDER_KEY)
                        || hasHtmlText((remarks as Record<string, string> | undefined)?.[COMMUNITY_STAKEHOLDER_KEY])) {
                        return true
                    }
                    return this.createError({
                        path: `${this.path}.${COMMUNITY_STAKEHOLDER_KEY}`,
                        message: 'Community Engagement Method is required',
                    })
                }
            ),
        }),
        [STAKEHOLDER_TABS.beneficiary_groups]: Yup.object({
            beneficiary_groups: requiredArray('beneficiary group').label('Beneficiary Groups'),
            beneficiary_groups_remarks: remarkPerOption('beneficiary_groups'),
        }),
        // Sponsors are saved from their popup, which validates each one.
        [STAKEHOLDER_TABS.institutional_sponsors]: Yup.object({
            institutional_sponsors: Yup.array()
                .min(1, 'At least one institutional sponsor must be added')
                .required()
                .label('Institutional Sponsors'),
        }),
    },
    preliminary_procurement_strategy: {
        [PROCUREMENT_TABS.major_inputs]: Yup.object({
            major_inputs_cost_coverage_percent: Yup.number()
                .transform((value, original) => (original === '' || original === null ? undefined : value))
                .typeError('Coverage must be a number')
                .required('Coverage of total project cost is required')
                .min(0, 'Coverage cannot be below 0%')
                .max(100, 'Coverage cannot be above 100%')
                .label('Coverage of total project cost'),
        }),
    },
    analysis_of_project_resource_requirements: {
        // Options save from their popup, which requires a description.
        [RESOURCE_TABS.project_alternatives]: Yup.object({
            project_alternatives: Yup.array()
                .min(1, 'At least one project option must be added')
                .required()
                .label('Project Alternatives'),
        }),
        // Estimates are saved on each option; the error is reported here so it marks the Cost Estimates tab.
        [RESOURCE_TABS.cost_estimates]: Yup.object({
            preliminary_cost_estimates: Yup.mixed().test(
                'financial-estimate-per-option',
                'Enter a financial estimate for every project option',
                function () {
                    const options = (this.parent.project_alternatives ?? []) as { cost_estimates?: Record<string, string> }[]
                    return options.every((option) => hasHtmlText(option.cost_estimates?.[FINANCIAL_ESTIMATE_KEY]))
                }
            ),
        }),
        [RESOURCE_TABS.sources_of_information]: Yup.object({
            sources_of_information: requiredString('Sources of Information (Verifiable)'),
        }),
        [RESOURCE_TABS.concept_report_plan]: Yup.object({
            concept_report_preparation_plan: requiredString('Detailed Plan for Preparation of the Concept Report'),
        }),
    },
    formulation_of_project_objectives_and_strategic_alignment: {
        // Popup rows are validated by the API on save; these tabs only need them to exist.
        [OBJECTIVES_TABS.project_objectives]: Yup.object({
            project_objectives_areas: projectObjectivesRule(),
        }),
        // Every End must keep a Means.
        [OBJECTIVES_TABS.means_to_end]: Yup.object({
            means_to_end_means: Yup.array()
                .min(1, 'At least one Means must be added')
                .required()
                .label('Means'),
            means_to_end_ends: Yup.array()
                .of(
                    Yup.object({
                        mean_ids: Yup.array().of(Yup.number()).min(1, 'At least one Means must be linked').required().label('Means'),
                    })
                )
                .min(1, 'At least one end must be added')
                .required()
                .label('Ends'),
        }),
        [OBJECTIVES_TABS.project_outputs]: Yup.object({
            project_outputs: Yup.array()
                .min(1, 'At least one project output must be added')
                .required()
                .label('Project Outputs'),
        }),
        [OBJECTIVES_TABS.project_outcomes]: Yup.object({
            project_outcomes: projectOutcomesRule(),
        }),
        // Optional estimates; the API stores whole numbers from 0.
        [OBJECTIVES_TABS.employment_quantification]: Yup.object({
            construction_jobs: optionalCount('Construction Jobs'),
            permanent_jobs: optionalCount('Permanent Jobs'),
            temporary_jobs: optionalCount('Temporary Jobs'),
        }),
        [OBJECTIVES_TABS.smart_indicators]: Yup.object({
            smart_indicators: smartIndicatorsRule(),
        }),
        // The three objective alignment narratives are optional.
        [OBJECTIVES_TABS.organisational_alignment]: Yup.object({
            organisational_goals: requiredArray('organisational goal').label('Organisational Goal'),
            policy_or_benchmark_alignment: requiredArray('policy or benchmark').label('Policy / Benchmark Alignment'),
            implementation_link: requiredString('Implementation Link', SHORT_FIELD_MAX_LENGTH),
            simple_contribution_statement: requiredString('Simple Contribution Statement', SHORT_FIELD_MAX_LENGTH),
            strategic_alignment: requiredString('Strategic Alignment'),
            alignment_strength: Yup.number()
                .min(1, 'Alignment Strength is required')
                .max(5)
                .required()
                .label('Alignment Strength'),
        }),
    },
}

// The whole step, for the steps' stage-level schema.
const allTabsSchema = (stepKey: string): Yup.AnyObjectSchema =>
    Object.values(stepTabSchemas[stepKey] ?? {}).reduce(
        (schema, tabSchema) => schema.concat(tabSchema),
        Yup.object(),
    )

// The first tab of a tab-wise step, in display order, that fails validation, with its errors.
export async function findIncompleteStepTab(stepKey: string, values: object): Promise<{ tabId: number; title: string; errors: FormikErrors<object> } | null> {
    for (const { tabId, title } of STEP_TABS[stepKey] ?? []) {
        const schema = stepTabSchemas[stepKey]?.[tabId]
        if (!schema) continue
        try {
            await schema.validate(values, { abortEarly: false })
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                return { tabId, title, errors: yupToFormErrors(error) }
            }
            throw error
        }
    }
    return null
}

export const projectInitiationStageSchema = {
    // The whole overview; the form uses projectOverviewSectionSchemas for the open tab.
    project_overview: Object.values(projectOverviewSectionSchemas).reduce(
        (schema, sectionSchema) => schema.concat(sectionSchema),
        Yup.object(),
    ),

    executive_summary: Yup.object({
        project_overview: requiredString('High-Level Project Overview'),
        vision: requiredString('Vision'),
        opportunity_definition: requiredString('Opportunity Definition'),
    }),

    ex_5w1h: Yup.object({
        ips_what: requiredString('IPS What'),
        ips_why: requiredString('IPS Why'),
        ips_who: requiredString('IPS Who'),
        ips_where: requiredString('IPS Where'),
        ips_when: requiredString('IPS When'),
        ips_how: requiredString('IPS How'),
    }),

    problem_identification: Yup.object({
        current_state_description: requiredString(
            'Current State Description / Status Quo Scenario'
        ),

        quantified_status_quo: requiredString(
            'Quantified Status Quo'
        ),

        wcis_wciip_infrastructure_categories: requiredArray('category')
            .label('WCIS / WCIIP Infrastructure Categories'),

        wcis_wciip_infrastructure_categories_remarks: Yup.object()
            .test(
                'asset-type',
                'Asset Type is required when Asset Failure Degradation is selected',
                function (remarks) {
                    const categories = this.parent.wcis_wciip_infrastructure_categories as string[] | undefined;
                    if (!categories?.includes('infrastructure_categories_3')) {
                        return true;
                    }

                    const assetType = (remarks as Record<string, string> | undefined)?.infrastructure_categories_3;
                    return Boolean(assetType?.trim());
                }
            ),

        cost_of_maintaining_status_quo: requiredString(
            'Cost of Maintaining Status Quo'
        ),

        with_project_scenario: requiredString(
            "'With Project' Scenario"
        ),

        desired_state_quantified: requiredString(
            'Desired State Quantified'
        ),
    }),

    // The whole step; the form uses stepTabSchemas for the open tab.
    problem_tree_analysis: allTabsSchema('problem_tree_analysis'),

    evidence_base: Yup.object({
        approved_data_sources: requiredArray('data source')
            .label('Approved Data Sources'),
    }),

    with_project_scenario: Yup.object({
        desired_state_description: requiredString(
            'Desired State Description'
        ),

        desired_state_quantified: requiredString(
            'Desired State Quantified'
        ),
    }),

    core_problem_and_gap_analysis: Yup.object({
        core_problem_statement: requiredString(
            'Core Problem Statement'
        ),

        cause_effect_logic: requiredString(
            'Cause-Effect Logic'
        ),

        consequences: requiredString(
            'Consequences If Problem Remains Unaddressed'
        ),
    }),

    sg0_key_activities_and_quests: Yup.object({
        key_activities_and_quests: requiredArray(
            'Key Activities and Quests'
        ),
    }),

    unique_value_proposition_and_competetive_advantage_or_investment_opportunity: Yup.object({
        customer_segment: requiredString('Customer Segment'),
        customer_need: requiredString('Customer Need'),
        unique_value_proposition: requiredString('Unique Value Proposition'),
        evidence_of_value: requiredString('Evidence of Value'),
        strategic_positioning: requiredString('Strategic Positioning'),
        competitive_advantage: requiredString('Competitive Advantage'),
    }),

    procurement_of_planning_resources: Yup.object({
        request_for_additional_funding: requiredString(
            'Request for Additional Funding'),
    }),

    documents: Yup.object({
        documents: Yup.array().min(1, 'At least one document is required').required().label('Documents'),
    }),

    strategic_need: Yup.object({
        strategic_need_narrative: requiredString(
            'Strategic Need Narrative'
        ),
        public_value_proposition: requiredString(
            'Public Value Proposition'
        ),
    }),

    // The whole step; the form uses stepTabSchemas for the open tab.
    preliminary_procurement_strategy: allTabsSchema('preliminary_procurement_strategy'),

    // The whole step; the form uses stepTabSchemas for the open tab.
    analysis_of_project_resource_requirements: allTabsSchema('analysis_of_project_resource_requirements'),

    // The whole step; the form uses stepTabSchemas for the open tab.
    formulation_of_project_objectives_and_strategic_alignment: allTabsSchema('formulation_of_project_objectives_and_strategic_alignment'),

    // The whole step; the form uses stepTabSchemas for the open tab.
    stakeholder_analysis_and_consultation: allTabsSchema('stakeholder_analysis_and_consultation'),

    constraints_and_risks: Yup.object({
        constraints_and_risks: requiredArray('Constraints and Risks'),
        constraints_and_risks_remarks: Yup.object().test(
            'remarks-required',
            'Remarks are required',
            (
                value?: Record<string, string>,
                context?: Yup.TestContext
            ) => {

                const selectedRisks: string[] =
                    context?.parent?.constraints_and_risks || [];

                for (const key of selectedRisks) {

                    const plainText = value?.[key]
                        ?.replace(/<[^>]*>/g, '')
                        ?.trim();

                    if (!plainText) {
                        return context?.createError({
                            path: `${context.path}.${key}`,
                            message: 'Remark is required',
                        });
                    }
                }

                return true;
            }
        ),
    }),
}

export type IProjectInitiationStageSchema =
    keyof typeof projectInitiationStageSchema